<template>
  <div class="w-100">
    <div class="w-100 d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center gap-3">
        <h5 class="mb-0">Recipient Addresses Book<span v-if="title" class="ms-2 text-muted fw-normal fs-6">{{ title }}</span></h5>
        <input
          v-model="search"
          type="text"
          class="form-control form-control-sm"
          placeholder="Search..."
          style="width: 220px"
          @input="onSearch"
        />
      </div>
      <div class="d-flex gap-2">
        <b-button variant="secondary" @click="openImport">
          <mdicon name="upload" size="16" />
        </b-button>
        <b-button variant="success" class="btn-green" @click="openCreate">
          <mdicon name="plus" size="16" />
        </b-button>
      </div>
    </div>

    <easy-data-table
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div style="text-align: center">No data available</div>
      </template>
      <template #item="{ column, item }: { column: string; item: AddressBook }">
        <span v-if="column === 'name'">{{ [item.firstName, item.lastName].filter(Boolean).join(' ') || '—' }}</span>
        <span v-if="column === 'company'">{{ item.company || '—' }}</span>
        <span v-if="column === 'countryCode'">{{ item.countryCode }}</span>
        <span v-if="column === 'city'">{{ item.city || '—' }}</span>
        <span v-if="column === 'addressLine'">{{ item.addressLine || '—' }}</span>
        <span v-if="column === 'phone'">{{ item.phoneNumber || '—' }}</span>
        <span v-if="column === 'email'">{{ item.email || '—' }}</span>
        <span v-if="column === 'actions'" class="d-flex gap-1">
          <b-button size="sm" variant="secondary" @click="openEdit(item)">
            <mdicon name="Pencil" size="16" />
          </b-button>
          <b-button size="sm" variant="danger" @click="confirmDelete(item)">
            <mdicon name="DeleteOutline" size="16" />
          </b-button>
        </span>
      </template>
    </easy-data-table>

    <TablePagination :server-items-length="serverItemsLength" :server-options-ref="serverOptions" :data-table-ref="dataTable" />

    <AddressBookFormModal />
    <AddressBookImportModal />
  </div>
</template>

<script lang="ts">
import type { AddressBook } from '@/types/Models/AddressBook'
import { BButton, BSpinner } from 'bootstrap-vue-next'
import { defineComponent, onMounted, onBeforeMount } from 'vue'
import { useAddressBookTable } from '@/composables/AddressBook/useAddressBookTable'
import { ADDRESS_BOOK_FORM, ADDRESS_BOOK_IMPORT } from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { openModal, registerModals } from '@/services/Modal'
import { useAddressBookStore } from '@/store/addressBook'
import EasyDataTable from 'vue3-easy-data-table'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import AddressBookFormModal from '@/views/Modals/AddressBook/AddressBookFormModal.vue'
import AddressBookImportModal from '@/views/Modals/AddressBook/AddressBookImportModal.vue'

export default defineComponent({
  name: 'AddressBookTable',
  components: { BButton, BSpinner, EasyDataTable, TablePagination, AddressBookFormModal, AddressBookImportModal },
  props: {
    companyId: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const addressBookStore = useAddressBookStore()

    const table = useAddressBookTable(() => props.companyId)

    const openCreate = () => {
      openModal(ADDRESS_BOOK_FORM, { companyId: props.companyId, addressId: null })
    }

    const openImport = () => {
      openModal(ADDRESS_BOOK_IMPORT, { companyId: props.companyId })
    }

    const openEdit = (item: AddressBook) => {
      openModal(ADDRESS_BOOK_FORM, { companyId: props.companyId, addressId: item.id })
    }

    const confirmDelete = (item: AddressBook) => {
      openModal('confirmWrap', {
        title: 'Delete Address',
        message: 'Are you sure you want to delete this address?',
        okButtonText: 'Delete',
        onConfirm: () => {
          addressBookStore.destroy(item.id).then(table.fetchData).catch(none)
        },
      })
    }

    onBeforeMount(() => {
      registerModals(ADDRESS_BOOK_FORM)
      registerModals(ADDRESS_BOOK_IMPORT)
    })

    onMounted(table.fetchData)

    return {
      ...table,
      headers: [...table.headers, { text: 'Actions', value: 'actions' }],
      openCreate,
      openImport,
      openEdit,
      confirmDelete,
    }
  },
})
</script>
